import { useEffect, useMemo, useState } from "react";
import { useQueries } from "@tanstack/react-query";
import { api } from "../../lib/api";
import type { TaskRun } from "../../../../shared/types";
import type { WorkflowStepModule, WorkflowStepRunState } from "../types";

type UseWorkflowStepRunsArgs = {
  storageKey: string | null;
  steps: WorkflowStepModule[];
};

type StepRunQuery = {
  data?: { data?: TaskRun } | undefined;
  error?: unknown;
  isLoading?: boolean;
};

export function loadStoredRunIds(storageKey: string | null): Record<string, string> {
  if (!storageKey || typeof window === "undefined") return {};
  try {
    const raw = window.sessionStorage.getItem(storageKey);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    return Object.fromEntries(
      Object.entries(parsed as Record<string, unknown>).filter(
        (entry): entry is [string, string] =>
          typeof entry[0] === "string" &&
          typeof entry[1] === "string" &&
          entry[1].trim().length > 0,
      ),
    );
  } catch {
    return {};
  }
}

export function workflowStepStatusFromRun(run: TaskRun | null): "idle" | "running" | "completed" | "failed" {
  return run?.status === "running" || run?.status === "retrying"
    ? "running"
    : run?.status === "completed"
      ? "completed"
      : run?.status === "failed"
        ? "failed"
        : "idle";
}

export function useWorkflowStepRuns({
  storageKey,
  steps,
}: UseWorkflowStepRunsArgs) {
  const [runIds, setRunIds] = useState<Record<string, string>>(() => loadStoredRunIds(storageKey));
  const [activeStepId, setActiveStepId] = useState<string | null>(null);

  useEffect(() => {
    setRunIds(loadStoredRunIds(storageKey));
  }, [storageKey]);

  useEffect(() => {
    if (!storageKey || typeof window === "undefined") return;
    if (Object.keys(runIds).length === 0) {
      window.sessionStorage.removeItem(storageKey);
      return;
    }
    window.sessionStorage.setItem(storageKey, JSON.stringify(runIds));
  }, [runIds, storageKey]);

  const runEntries = useMemo(
    () => steps.flatMap((step) => {
      const runId = runIds[step.id];
      return runId ? [{ stepId: step.id, runId }] : [];
    }),
    [runIds, steps],
  );

  const queries = useQueries({
    queries: runEntries.map(({ stepId, runId }) => ({
      queryKey: ["task-run", runId],
      queryFn: () => api.get<{ data: TaskRun }>(`/api/task-runs/${encodeURIComponent(runId)}`),
      enabled: !!runId,
      staleTime: 10_000,
      refetchInterval: (query: { state: { data?: { data?: TaskRun } } }) => {
        const status = query.state.data?.data?.status;
        return status === "running" || status === "retrying" ? 3000 : false;
      },
      meta: { stepId },
    })),
  });

  const queryByStepId = useMemo(
    () => new Map(
      runEntries.map(({ stepId }, index) => [stepId, queries[index] as StepRunQuery] as const),
    ),
    [queries, runEntries],
  );

  function startStep(stepId: string): void {
    setActiveStepId(stepId);
  }

  function finishStep(stepId: string, runId?: string): void {
    if (runId) {
      setRunIds((current) => ({ ...current, [stepId]: runId }));
    }
    setActiveStepId((current) => (current === stepId ? null : current));
  }

  function getStepState<TSnapshot>(
    step: WorkflowStepModule<TSnapshot>,
  ): WorkflowStepRunState<TSnapshot> {
    const runId = runIds[step.id] ?? null;
    const query = queryByStepId.get(step.id);
    const run = query?.data?.data ?? null;
    const snapshot = step.extractSnapshot(run);
    const error = query?.error instanceof Error ? query.error : null;
    return {
      runId,
      run,
      snapshot,
      status: workflowStepStatusFromRun(run),
      isLoading: Boolean(query?.isLoading),
      error,
      isActive: activeStepId === step.id,
    };
  }

  return {
    startStep,
    finishStep,
    getStepState,
    getRunId: (stepId: string) => runIds[stepId] ?? null,
  };
}
